/**
 * Transaction History page.
 * Displays list of all configuration uploads with filters, sorting, and pagination.
 */

import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import { FileUpload } from '@/components/Upload/FileUpload';
import { api } from '@/services/api';
import type { Transaction } from '@/types/entities';
import { useAuthStore } from '@/stores/authStore';

export const TransactionHistory: FC = () => {
  // Get current user from auth store
  const user = useAuthStore((state) => state.user);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [orderBy, setOrderBy] = useState<keyof Transaction>('uploadTimestamp');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Filter state
  const [filters, setFilters] = useState({
    uploader: '',
    startDate: null as Dayjs | null,
    endDate: null as Dayjs | null,
  });

  // Data state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);

  // Extract unique uploaders for filter dropdown
  const uniqueUploaders = useMemo(() => {
    return Array.from(new Set(transactions.map((t) => t.uploaderUsername)));
  }, [transactions]);

  // Fetch transactions from the API
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: {
        limit: number;
        offset: number;
        orderBy: string;
        order: 'asc' | 'desc';
        uploader?: string;
        startDate?: string;
        endDate?: string;
      } = {
        limit: pageSize,
        offset: page * pageSize,
        orderBy,
        order,
      };

      // Add filters
      if (filters.uploader) params.uploader = filters.uploader;
      if (filters.startDate) params.startDate = filters.startDate.toISOString();
      if (filters.endDate) params.endDate = filters.endDate.toISOString();

      const data = await api.getTransactions(params);
      setTransactions(data.transactions);
      setTotalCount(data.total);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Failed to load transactions. Please check if the backend is running.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, orderBy, order, filters]);

  // Fetch transactions on mount and when dependencies change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Handle sort
  const handleSort = (property: keyof Transaction) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting changes
  };

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleFilterChange = (field: string, value: string | Dayjs | null) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      uploader: '',
      startDate: null,
      endDate: null,
    });
    setPage(0);
  };

  // Handle view details
  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  // Handle download
  const handleDownload = async (transaction: Transaction) => {
    setDownloading(transaction.transactionId);
    try {
      const blob = await api.downloadTransactionFile(transaction.transactionId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = transaction.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(`Failed to download ${transaction.filename}`);
    } finally {
      setDownloading(null);
    }
  };

  // Handle successful upload (callback for FileUpload component)
  const handleUploadSuccess = () => {
    fetchTransactions();
  };

  // Format file size from bytes to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Format timestamp to readable date/time
  const formatTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status chip color
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'failed':
        return 'error';
      case 'deleted':
        return 'default';
      default:
        return 'warning';
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  // Handle delete transaction
  const handleDelete = async () => {
    if (!selectedTransaction) return;

    setDeleting(selectedTransaction.transactionId);
    setDeleteDialogOpen(false);

    try {
      await api.deleteTransaction(selectedTransaction.transactionId, user?.username || 'system');

      // Refresh the transactions list
      await fetchTransactions();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(`Failed to delete ${selectedTransaction.filename}`);
    } finally {
      setDeleting(null);
      setSelectedTransaction(null);
    }
  };

  // Count deleted transactions
  const deletedCount = useMemo(() => {
    return transactions.filter((t) => t.status === 'deleted').length;
  }, [transactions]);

  // Handle empty trash
  const handleEmptyTrash = async () => {
    setEmptyingTrash(true);
    setEmptyTrashDialogOpen(false);

    try {
      const result = await api.emptyTrash();

      // Show success message
      console.log('Empty trash result:', result);

      // Refresh the transactions list
      await fetchTransactions();

      // Show summary
      if (result.deletedCount > 0) {
        const sizeMB = (result.totalSizeFreed / (1024 * 1024)).toFixed(2);
        alert(`Successfully emptied trash!\n\n${result.deletedCount} files permanently deleted\n${sizeMB} MB freed`);
      } else {
        alert('Trash is already empty.');
      }
    } catch (err) {
      console.error('Empty trash failed:', err);
      setError('Failed to empty trash. Please try again.');
    } finally {
      setEmptyingTrash(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Transaction History
        </Typography>

        {/* File Upload Component */}
        <FileUpload onUploadSuccess={handleUploadSuccess} />

        {/* Filters Card */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={uniqueUploaders}
                  value={filters.uploader}
                  onChange={(_, value) => handleFilterChange('uploader', value || '')}
                  renderInput={(params) => <TextField {...params} label="Uploader" size="small" />}
                  freeSolo
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearFilters}
                  size="medium"
                  sx={{ height: '40px' }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Transactions Table Card */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Configuration Uploads</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  size="small"
                  onClick={fetchTransactions}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  size="small"
                  onClick={() => setEmptyTrashDialogOpen(true)}
                  disabled={deletedCount === 0 || emptyingTrash}
                >
                  {emptyingTrash ? 'Emptying...' : `Empty Trash (${deletedCount})`}
                </Button>
              </Box>
            </Box>

            {/* Error State */}
            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                <Button size="small" onClick={fetchTransactions} sx={{ ml: 2 }}>
                  Retry
                </Button>
              </Alert>
            )}

            {/* Empty State */}
            {!loading && !error && transactions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No transactions found. Upload a configuration file to see it here.
              </Typography>
            )}

            {/* Transaction Table */}
            {!error && (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Transaction ID</strong>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'filename' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'filename'}
                            direction={orderBy === 'filename' ? order : 'asc'}
                            onClick={() => handleSort('filename')}
                          >
                            <strong>Filename</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'fileSize' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'fileSize'}
                            direction={orderBy === 'fileSize' ? order : 'asc'}
                            onClick={() => handleSort('fileSize')}
                          >
                            <strong>Size</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'uploadTimestamp' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'uploadTimestamp'}
                            direction={orderBy === 'uploadTimestamp' ? order : 'asc'}
                            onClick={() => handleSort('uploadTimestamp')}
                          >
                            <strong>Upload Time</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'uploaderUsername' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'uploaderUsername'}
                            direction={orderBy === 'uploaderUsername' ? order : 'asc'}
                            onClick={() => handleSort('uploaderUsername')}
                          >
                            <strong>Uploader</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        // Loading skeleton
                        Array.from({ length: pageSize }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <CircularProgress size={16} />
                            </TableCell>
                            <TableCell>Loading...</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        transactions.map((transaction) => (
                          <TableRow key={transaction.transactionId} hover>
                            <TableCell>
                              <Tooltip title={transaction.transactionId}>
                                <Chip
                                  label={transaction.transactionId.substring(0, 8)}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => copyToClipboard(transaction.transactionId)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>{transaction.filename}</TableCell>
                            <TableCell>{formatFileSize(transaction.fileSize)}</TableCell>
                            <TableCell>{formatTimestamp(transaction.uploadTimestamp)}</TableCell>
                            <TableCell>{transaction.uploaderUsername}</TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.status}
                                color={getStatusColor(transaction.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(transaction)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download File">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(transaction)}
                                  disabled={downloading === transaction.transactionId}
                                >
                                  {downloading === transaction.transactionId ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={transaction.status === 'deleted' ? 'Already deleted' : 'Delete Transaction'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(transaction)}
                                    disabled={
                                      transaction.status === 'deleted' ||
                                      deleting === transaction.transactionId
                                    }
                                    color="error"
                                  >
                                    {deleting === transaction.transactionId ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Transaction Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Transaction Details
            <IconButton
              onClick={() => setDetailsOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transaction ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedTransaction.transactionId}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(selectedTransaction.transactionId)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Filename
                  </Typography>
                  <Typography variant="body1">{selectedTransaction.filename}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    File Size
                  </Typography>
                  <Typography variant="body1">
                    {formatFileSize(selectedTransaction.fileSize)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Upload Time
                  </Typography>
                  <Typography variant="body1">
                    {formatTimestamp(selectedTransaction.uploadTimestamp)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Uploader
                  </Typography>
                  <Typography variant="body1">{selectedTransaction.uploaderUsername}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedTransaction.status}
                    color={getStatusColor(selectedTransaction.status)}
                    size="small"
                  />
                </Grid>

                {/* Error Message - only show for failed status */}
                {selectedTransaction.status === 'failed' && selectedTransaction.errorMessage && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error.main" gutterBottom>
                      Error Message
                    </Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        {selectedTransaction.errorMessage}
                      </Typography>
                      {selectedTransaction.errorTimestamp && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Error occurred at: {formatTimestamp(selectedTransaction.errorTimestamp)}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}

                {/* Entity Counts - only show for completed status */}
                {selectedTransaction.status === 'completed' && selectedTransaction.entitiesCreated && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Entities Created
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedTransaction.entitiesCreated.switches !== undefined && (
                        <Chip
                          label={`${selectedTransaction.entitiesCreated.switches} Switch${selectedTransaction.entitiesCreated.switches !== 1 ? 'es' : ''}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {selectedTransaction.entitiesCreated.interfaces !== undefined && (
                        <Chip
                          label={`${selectedTransaction.entitiesCreated.interfaces} Interface${selectedTransaction.entitiesCreated.interfaces !== 1 ? 's' : ''}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {selectedTransaction.entitiesCreated.vlan_configs !== undefined && (
                        <Chip
                          label={`${selectedTransaction.entitiesCreated.vlan_configs} VLAN Config${selectedTransaction.entitiesCreated.vlan_configs !== 1 ? 's' : ''}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {selectedTransaction.entitiesCreated.canonical_vlans !== undefined && (
                        <Chip
                          label={`${selectedTransaction.entitiesCreated.canonical_vlans} Canonical VLAN${selectedTransaction.entitiesCreated.canonical_vlans !== 1 ? 's' : ''}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {selectedTransaction.entitiesCreated.relationships !== undefined && (
                        <Chip
                          label={`${selectedTransaction.entitiesCreated.relationships} Relationship${selectedTransaction.entitiesCreated.relationships !== 1 ? 's' : ''}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    File Hash (SHA-256)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    {selectedTransaction.fileHash}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Storage Path
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    {selectedTransaction.storagePath}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            {selectedTransaction && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleDownload(selectedTransaction);
                  setDetailsOpen(false);
                }}
              >
                Download File
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Configuration File?</DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This action will mark the transaction as deleted.
                </Alert>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete this transaction?
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Filename:</strong> {selectedTransaction.filename}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Transaction ID:</strong> {selectedTransaction.transactionId.substring(0, 8)}...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Uploaded:</strong> {formatTimestamp(selectedTransaction.uploadTimestamp)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>Note:</strong> The configuration file will be preserved on disk, and graph nodes
                  will remain in the database for historical queries. Only the transaction metadata will be
                  marked as deleted.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Empty Trash Confirmation Dialog */}
        <Dialog
          open={emptyTrashDialogOpen}
          onClose={() => setEmptyTrashDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteForeverIcon color="error" />
              Empty Trash?
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              ⚠️ WARNING: This action is PERMANENT and IRREVERSIBLE!
            </Alert>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
              You are about to permanently delete {deletedCount} file{deletedCount !== 1 ? 's' : ''} from storage.
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1, color: 'error.contrastText' }}>
              <Typography variant="body2" gutterBottom>
                <strong>What will happen:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Configuration files will be PERMANENTLY deleted from disk
              </Typography>
              <Typography variant="body2" component="div">
                • Transaction metadata files will be PERMANENTLY deleted
              </Typography>
              <Typography variant="body2" component="div">
                • Files CANNOT be recovered after deletion
              </Typography>
            </Box>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>What will be preserved:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Graph nodes remain in database
              </Typography>
              <Typography variant="body2" component="div">
                • Historical queries continue to work
              </Typography>
              <Typography variant="body2" component="div">
                • Network topology data intact
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mt: 3, fontWeight: 'bold', color: 'error.main' }}>
              Are you absolutely sure you want to permanently delete these files?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmptyTrashDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={handleEmptyTrash}
            >
              Yes, Permanently Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};
